import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { FaBars } from "react-icons/fa";
import CategoryList from "./CategoryList";
import ProductCard from "../components/ProductCard";
import "../styles/home.css";

export default function Home() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const { categoryId } = useParams();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;

  useEffect(() => {
    if (categoryId) {
      setSelectedCategory(categoryId);
      setPage(0);
    } else {
      setSelectedCategory("");
    }
  }, [categoryId]);

  useEffect(() => {
    axios.get(PRODUCT_API + "/products/categories/").then((res) => {
      setCategories(res.data?.content || []);
    });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(PRODUCT_API + "/products/buyers", {
        params: {
          page,
          size: pageSize,
          sortField: sortField || undefined,
          sortOrder,
          categoryId: selectedCategory || undefined,
        },
      });

      const content = res.data?.content || [];
      setProducts(content);
      setTotalPages(res.data?.totalPages || 0);
    } catch (err) {
      console.error("Failed to fetch data");
      setError("Failed to fetch products or categories.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortField, sortOrder, selectedCategory, PRODUCT_API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [selectedCategory]);

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="container-fluid px-0">
      <div className="row g-0">
        {/* ===================== */}
        {/* Sidebar (Desktop) */}
        {/* ===================== */}
        <div className="col-lg-3 d-none d-lg-block border-end bg-white shadow-sm category-sidebar">
          <div className="p-3">
            <h6 className="fw-bold mb-3">Categories</h6>
            <CategoryList
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={(id) => {
                navigate(`/category/${id}`);
                setPage(0);
              }}
              itemProps={{ "data-bs-dismiss": "offcanvas" }}
            />
          </div>
        </div>

        {/* ===================== */}
        {/* Mobile Sidebar Drawer */}
        {/* ===================== */}
        <div
          className="offcanvas offcanvas-start"
          tabIndex="-1"
          id="categorySidebar"
        >
          <div className="offcanvas-header border-bottom">
            <h6 className="fw-bold">Categories</h6>
            <button className="btn-close" data-bs-dismiss="offcanvas"></button>
          </div>
          <div className="offcanvas-body">
            <CategoryList
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={(id) => {
                navigate(`/category/${id}`);
                setPage(0);
              }}
              itemProps={{ "data-bs-dismiss": "offcanvas" }}
            />
          </div>
        </div>

        {/* ===================== */}
        {/* Main Content */}
        {/* ===================== */}
        <div className="col-12 col-lg-9">
          {/* Mobile Top Bar */}
          <div className="d-lg-none d-flex align-items-center border-bottom p-3 bg-white shadow-sm">
            <button
              className="btn p-0 me-3"
              data-bs-toggle="offcanvas"
              data-bs-target="#categorySidebar"
            >
              <FaBars size={22} />
            </button>
            <h6 className="fw-bold mb-0">Categories</h6>
          </div>

          {/* Hero Section */}
          <div
            className="p-4 p-lg-5 text-white"
            style={{
              background: "linear-gradient(135deg, #0d6efd, #6610f2)",
              borderRadius: "0 0 20px 20px",
            }}
          >
            <h2 className="fw-bold">Welcome to Afro Gebeya</h2>
            <p className="opacity-75 mb-0">
              Discover amazing products from trusted sellers.
            </p>
          </div>
          <div className="row g-3">
            {loading
              ? Array.from({ length: pageSize }).map((_, idx) => (
                  <div className="col-6 col-sm-4 col-md-3" key={idx}>
                    <div className="card shadow-sm mb-3">
                      <div className="skeleton-image mb-2"></div>
                      <div className="card-body p-2">
                        <div className="skeleton-text mb-1"></div>
                        <div className="skeleton-text short"></div>
                      </div>
                    </div>
                  </div>
                ))
              : products.map((product) => (
                  <div className="col-6 col-sm-4 col-md-3" key={product.id}>
                    <ProductCard product={product} />
                  </div>
                ))}
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="d-flex justify-content-center mt-4">
          <button
            className="btn btn-outline-primary me-2"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="align-self-center fw-bold">
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn btn-outline-primary ms-2"
            onClick={() => setPage(page + 1)}
            disabled={page + 1 >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
